import React, { useCallback, useEffect, useMemo, useState } from 'react';
import DashboardCard from '../../../shared/ui/cards/DashboardCard';
import { BottomSheet, HorizontalCategoryTabs, UserList, type User as UserListUser } from '../../../shared/ui';
import { stationsApi, KeyfobAssignment, Station, StationEvent } from '../../../api/stationsApi';
import { siteExecutorsApi, SiteExecutor } from '../../../api/siteExecutorsApi';
import { sitesV2Api } from '../../../api/sitesV2Api';
import { siteMemberPermissionsApi } from '../../../api/siteMemberPermissionsApi';
import { useAuth } from '../../../shared/contexts/AuthContext';
import config from '../../../config/config';
import { useToast } from '../../../shared/contexts/ToastContext';
import './StationsCard.css';

function formatStationsApiError(e: unknown): string {
  const err = e as { response?: { data?: unknown }; message?: string; code?: string };
  const d = err?.response?.data;
  let msg =
    typeof d === 'string'
      ? d
      : d && typeof d === 'object' && 'message' in d
        ? String((d as { message?: string }).message)
        : err?.message || 'Ошибка';
  if (err?.code === 'ERR_NETWORK' || /network error/i.test(String(msg))) {
    return 'Нет связи с сервером. Проверьте интернет и обновите страницу.';
  }
  return String(msg);
}

const EXECUTOR_AVATAR_COLORS = [
  '#4A90E2', '#50C878', '#FF6B6B', '#FFA500', '#9B59B6', '#E74C3C', '#3498DB', '#2ECC71',
] as const;

/** Совпадает с KeyfobShiftAutomation.SilenceMinutesBeforeAutoClose в appsettings, если у станции не задано своё. */
const DEFAULT_FOB_SHIFT_SILENCE_MIN = 5;
const DEFAULT_FOB_SHIFT_OFFLINE_GRACE_MIN = 30;
const DEFAULT_FOB_SHIFT_REOPEN_WINDOW_MIN = 10;

const FOB_SHIFT_SILENCE_MINUTES_OPTIONS = [1, 2, 3, 5, 10, 15, 20, 30, 45, 60] as const;
const FOB_SHIFT_OFFLINE_GRACE_MINUTES_OPTIONS = [5, 10, 15, 30, 45, 60, 90, 120] as const;
const FOB_SHIFT_REOPEN_WINDOW_MINUTES_OPTIONS = [1, 2, 3, 5, 10, 15, 20, 30] as const;

function stationFobShiftSilenceMinutes(s: Station): number {
  const v = s.fobShiftSilenceMinutes;
  if (typeof v === 'number' && Number.isFinite(v) && v > 0) return v;
  return DEFAULT_FOB_SHIFT_SILENCE_MIN;
}

function stationFobShiftOfflineGraceMinutes(s: Station): number {
  const v = s.fobShiftOfflineGraceMinutes;
  if (typeof v === 'number' && Number.isFinite(v) && v > 0) return v;
  return DEFAULT_FOB_SHIFT_OFFLINE_GRACE_MIN;
}

function stationFobShiftReopenWindowMinutes(s: Station): number {
  const v = s.fobShiftReopenWindowMinutes;
  if (typeof v === 'number' && Number.isFinite(v) && v > 0) return v;
  return DEFAULT_FOB_SHIFT_REOPEN_WINDOW_MIN;
}

function getExecutorAvatarColor(executorId: number): string {
  return EXECUTOR_AVATAR_COLORS[Math.abs(executorId) % EXECUTOR_AVATAR_COLORS.length];
}

/** Метка с r-station после автопривязки — в списке брелков не показываем */
const HIDDEN_FOB_LABELS = new Set(['pair', 'bind']);

function sanitizeKeyfobDisplayLabel(label: string | null | undefined): string | null {
  if (label == null) return null;
  const t = String(label).trim();
  if (!t) return null;
  if (HIDDEN_FOB_LABELS.has(t.toLowerCase())) return null;
  return t;
}

/** MAC из кэша сервера (как в watchlist r-station: с двоеточиями). */
function isHeartbeatBleMac(addr: string): boolean {
  const t = String(addr ?? '')
    .trim()
    .toUpperCase();
  if (t.length >= 17) return true;
  const compact = t.replace(/[:\s-]/g, '');
  return /^[0-9A-F]{12}$/.test(compact);
}

/** Тот же вид ключа, что и в Keyfobs.Uid / keyfob-assignments (MAC upper). */
function credentialUidFromBle(mac: string): string {
  return String(mac ?? '')
    .trim()
    .replace(/^0x/i, '')
    .toUpperCase();
}

/** Единое окно «станция на связи» (совпадает с heartbeat с r-station и порогом счётчика онлайн в шапке). */
const STATION_HEARTBEAT_FRESH_MS = 90 * 1000;
/** Окно «в зоне» для телефона (шире брелка — heartbeat раз в ~1 мин, возможны паузы в Doze). */
const MOBILE_PRESENCE_ONLINE_FRESH_MS = 15 * 60 * 1000;
/** Окно «в зоне» для брелка / anti-lost. */
const PRESENCE_ONLINE_FRESH_MS = DEFAULT_FOB_SHIFT_SILENCE_MIN * 60 * 1000;

type PresenceSnap = {
  online: boolean;
  lastSeenUtc: string | null;
  source: 'rfob' | 'antilost' | 'mobile';
};

function presenceFresh(utc?: string | null, freshMs: number = PRESENCE_ONLINE_FRESH_MS): boolean {
  if (!utc) return false;
  const ts = new Date(utc).getTime();
  return Number.isFinite(ts) && Date.now() - ts < freshMs;
}

function mergePresenceSnapshots(a: PresenceSnap | null, b: PresenceSnap | null): PresenceSnap | null {
  if (!a) return b;
  if (!b) return a;
  const aTs = a.lastSeenUtc ? new Date(a.lastSeenUtc).getTime() : 0;
  const bTs = b.lastSeenUtc ? new Date(b.lastSeenUtc).getTime() : 0;
  return bTs >= aTs ? b : a;
}

function upsertCredentialPresence(
  map: Map<string, PresenceSnap>,
  uid: string,
  snap: PresenceSnap
) {
  const prev = map.get(uid);
  if (!prev) {
    map.set(uid, snap);
    return;
  }
  const prevTs = prev.lastSeenUtc ? new Date(prev.lastSeenUtc).getTime() : 0;
  const newTs = snap.lastSeenUtc ? new Date(snap.lastSeenUtc).getTime() : 0;
  if (newTs >= prevTs) map.set(uid, snap);
  else if (snap.online && !prev.online) map.set(uid, { ...prev, online: true });
}

function formatEventActorName(
  keyfobUid: string | null | undefined,
  userNameByUserId: Map<number, string>
): string {
  if (!keyfobUid) return '—';
  const mobile = keyfobUid.match(/^MOBILE:(\d+)$/i);
  if (mobile) {
    const userId = Number(mobile[1]);
    const name = userNameByUserId.get(userId);
    return name ?? `Пользователь #${userId}`;
  }
  return keyfobUid;
}

function formatEventTime(utc: string | null | undefined): string {
  if (!utc) return '—';
  const d = new Date(utc);
  if (!Number.isFinite(d.getTime())) return '—';
  const today = new Date();
  const sameDay =
    d.getFullYear() === today.getFullYear() &&
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate();
  if (sameDay) return d.toLocaleTimeString();
  return d.toLocaleString();
}

function formatLastSeen(utc?: string | null): string | null {
  if (!utc) return null;
  const ts = new Date(utc).getTime();
  if (!Number.isFinite(ts)) return null;
  const diffMs = Date.now() - ts;
  if (diffMs < 0) return null;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMs < 10000) return 'только что';
  if (diffMin < 1) return `${Math.floor(diffMs / 1000)} сек назад`;
  if (diffMin < 60) return `${diffMin} мин назад`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH} ч назад`;
  return `${Math.floor(diffH / 24)} д назад`;
}

function stationHeartbeatFresh(lastSeenAt?: string | null): boolean {
  if (!lastSeenAt) return false;
  const ts = new Date(lastSeenAt).getTime();
  return Number.isFinite(ts) && Date.now() - ts < STATION_HEARTBEAT_FRESH_MS;
}

function PresenceDot({ online, title }: { online: boolean; title: string }) {
  return (
    <span
      className={`station-presence-dot${online ? ' station-presence-dot--online' : ''}`}
      title={title}
      role="img"
      aria-label={title}
    />
  );
}

type UnboundPickRow =
  | { kind: 'rfob'; stationName: string; keyfobUid: string; label?: string | null }
  | { kind: 'antilost'; stationName: string; keyfobUid: string; label?: string | null };

interface StationsCardProps {
  siteId?: number;
}

function generateApiKey(): string {
  const buf = new Uint8Array(24);
  crypto.getRandomValues(buf);
  return Array.from(buf, (b) => b.toString(16).padStart(2, '0')).join('');
}

async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through */
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
    return true;
  } catch {
    return false;
  }
}

interface SecretPayload {
  title: string;
  deviceUid?: string;
  apiKey: string;
  beaconPublicId?: string;
  hint?: string;
}

const STATIONS_TABS = ['Брелки', 'Станции', 'Привязка', 'Лог'] as const;
type StationsTab = (typeof STATIONS_TABS)[number];

const StationsCard: React.FC<StationsCardProps> = ({ siteId }) => {
  const { showError } = useToast();
  const { user } = useAuth();
  const [hasSiteEditPermission, setHasSiteEditPermission] = useState<boolean | null>(null);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [isFullscreenOpen, setIsFullscreenOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<StationsTab>('Брелки');
  const [stations, setStations] = useState<Station[]>([]);
  const [events, setEvents] = useState<StationEvent[]>([]);
  const [keyfobAssignments, setKeyfobAssignments] = useState<KeyfobAssignment[]>([]);
  const [executors, setExecutors] = useState<SiteExecutor[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [kfSubmitting, setKfSubmitting] = useState(false);
  const [bindExecutorId, setBindExecutorId] = useState<number | null>(null);

  const [showAddModal, setShowAddModal] = useState(false);
  const [formName, setFormName] = useState('');
  const [formBeaconId, setFormBeaconId] = useState('');
  const [formApiKey, setFormApiKey] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [secretShown, setSecretShown] = useState<SecretPayload | null>(null);
  const [settingsForStationId, setSettingsForStationId] = useState<number | null>(null);
  const [settingsNameDraft, setSettingsNameDraft] = useState('');
  const [settingsBeaconDraft, setSettingsBeaconDraft] = useState('');
  const [settingsSaveBusy, setSettingsSaveBusy] = useState(false);
  /** Какая станция сейчас сохраняет флаг учёта по сменам (список или лист настроек). */
  const [stationShiftsToggleBusyId, setStationShiftsToggleBusyId] = useState<number | null>(null);
  const [settingsSilenceMinutes, setSettingsSilenceMinutes] = useState(DEFAULT_FOB_SHIFT_SILENCE_MIN);
  const [settingsOfflineGraceMinutes, setSettingsOfflineGraceMinutes] = useState(DEFAULT_FOB_SHIFT_OFFLINE_GRACE_MIN);
  const [settingsReopenWindowMinutes, setSettingsReopenWindowMinutes] = useState(DEFAULT_FOB_SHIFT_REOPEN_WINDOW_MIN);
  const [stationSilenceSaveBusyId, setStationSilenceSaveBusyId] = useState<number | null>(null);
  const [stationOfflineGraceSaveBusyId, setStationOfflineGraceSaveBusyId] = useState<number | null>(null);
  const [stationReopenWindowSaveBusyId, setStationReopenWindowSaveBusyId] = useState<number | null>(null);
  const [fobShiftBusyUid, setFobShiftBusyUid] = useState<string | null>(null);

  useEffect(() => {
    if (!siteId || !user?.id) {
      setHasSiteEditPermission(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const members = await sitesV2Api.getSiteMembers(siteId);
        const currentUserMember = members.find((m) => m.userId === user.id);
        if (!currentUserMember) {
          if (!cancelled) setHasSiteEditPermission(false);
          return;
        }
        const perms = await siteMemberPermissionsApi.getMemberPermissions(siteId, currentUserMember.id);
        if (!cancelled) {
          setHasSiteEditPermission(perms.some((p) => p.permissionCode === 'SITE_EDIT'));
        }
      } catch {
        if (!cancelled) setHasSiteEditPermission(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [siteId, user?.id]);

  const loadData = useCallback(async () => {
    if (!siteId) return;
    try {
      setLoading(true);
      setError(null);
      const [stationsData, eventsData, kfData, exData] = await Promise.all([
        stationsApi.getStations(siteId),
        stationsApi.getStationEvents(siteId, { limit: 100 }),
        stationsApi.getKeyfobAssignments(siteId, true).catch(() => [] as KeyfobAssignment[]),
        siteExecutorsApi.getSiteExecutors(siteId).catch(() => [] as SiteExecutor[]),
      ]);
      setStations(stationsData);
      setEvents(eventsData);
      setKeyfobAssignments(kfData);
      setExecutors(exData);
    } catch (e: any) {
      console.error('Error loading stations card:', e);
      if (e?.response?.status === 403) {
        setHasSiteEditPermission(false);
        return;
      }
      const msg =
        typeof e?.response?.data === 'string'
          ? e.response.data
          : e?.response?.data?.message || e?.message || 'Ошибка загрузки станций';
      setError(String(msg));
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => {
    if (siteId && (!isCollapsed || isFullscreenOpen)) {
      void loadData();
    }
  }, [isCollapsed, isFullscreenOpen, siteId, loadData]);

  useEffect(() => {
    if (!siteId || (isCollapsed && !isFullscreenOpen) || activeTab !== 'Лог') return;
    const id = setInterval(() => { void loadData(); }, 30_000);
    return () => clearInterval(id);
  }, [siteId, isCollapsed, isFullscreenOpen, activeTab, loadData]);

  useEffect(() => {
    setSettingsForStationId(null);
    setBindExecutorId(null);
    setActiveTab('Брелки');
  }, [siteId]);

  const settingsStation = useMemo(
    () => (settingsForStationId != null ? stations.find((s) => s.id === settingsForStationId) : undefined),
    [stations, settingsForStationId]
  );

  /** Вкл — сервер открывает/продлевает смены по брелку с этой станции (blockFobShiftAutomation = false). */
  const stationShiftsActive = (s: Station) => !s.blockFobShiftAutomation;

  const openStationSettings = (s: Station) => {
    setSettingsForStationId(s.id);
    setSettingsNameDraft(s.name);
    setSettingsBeaconDraft(s.beaconPublicId ?? '');
    setSettingsSilenceMinutes(stationFobShiftSilenceMinutes(s));
    setSettingsOfflineGraceMinutes(stationFobShiftOfflineGraceMinutes(s));
    setSettingsReopenWindowMinutes(stationFobShiftReopenWindowMinutes(s));
  };

  const closeStationSettings = () => {
    if (
      settingsSaveBusy ||
      stationShiftsToggleBusyId !== null ||
      stationSilenceSaveBusyId !== null ||
      stationOfflineGraceSaveBusyId !== null ||
      stationReopenWindowSaveBusyId !== null
    )
      return;
    setSettingsForStationId(null);
  };

  useEffect(() => {
    if (settingsStation) {
      setSettingsSilenceMinutes(stationFobShiftSilenceMinutes(settingsStation));
      setSettingsOfflineGraceMinutes(stationFobShiftOfflineGraceMinutes(settingsStation));
      setSettingsReopenWindowMinutes(stationFobShiftReopenWindowMinutes(settingsStation));
    }
  }, [
    settingsStation?.id,
    settingsStation?.fobShiftSilenceMinutes,
    settingsStation?.fobShiftOfflineGraceMinutes,
    settingsStation?.fobShiftReopenWindowMinutes,
  ]);

  const saveStationBeaconId = async () => {
    if (!siteId || !settingsStation) return;
    const beacon = settingsBeaconDraft.trim().toUpperCase();
    const current = (settingsStation.beaconPublicId ?? '').toUpperCase();
    if (beacon === current) return;
    if (beacon && !/^[0-9A-F]{6}$/.test(beacon)) {
      window.alert('ID маяка: 6 hex-символов, напр. B23810.');
      return;
    }
    if (current && beacon && current !== beacon) {
      const ok = window.confirm(
        `Сменить ID маяка с ${current} на ${beacon}? Секрет на сервере не меняется — если это другая плата, после сохранения нажмите «Новый секрет маяка».`
      );
      if (!ok) return;
    }
    try {
      setSettingsSaveBusy(true);
      const res = await stationsApi.updateStation(siteId, settingsStation.id, { beaconPublicId: beacon });
      await loadData();
      const serverSecret = res.beaconSecret?.trim();
      if (serverSecret && beacon) {
        setSecretShown({
          title: 'ID маяка сохранён',
          apiKey: serverSecret,
          beaconPublicId: beacon,
          hint: `ID ${beacon}. Скопируйте секрет в портал станции (кнопка 3 сек → «Секрет маяка»).`,
        });
      }
    } catch (e: any) {
      const d = e?.response?.data;
      const msg = typeof d === 'string' ? d : d?.message || e?.message || 'Ошибка сохранения';
      window.alert(String(msg));
    } finally {
      setSettingsSaveBusy(false);
    }
  };

  const saveStationName = async () => {
    if (!siteId || !settingsStation) return;
    const name = settingsNameDraft.trim();
    if (!name) {
      window.alert('Введите название станции.');
      return;
    }
    if (name === settingsStation.name) return;
    try {
      setSettingsSaveBusy(true);
      await stationsApi.updateStation(siteId, settingsStation.id, { name });
      await loadData();
    } catch (e: any) {
      const d = e?.response?.data;
      const msg = typeof d === 'string' ? d : d?.message || e?.message || 'Ошибка сохранения';
      window.alert(String(msg));
    } finally {
      setSettingsSaveBusy(false);
    }
  };

  const saveStationSilenceMinutes = async (stationId: number, minutes: number) => {
    if (!siteId) return;
    const st = stations.find((s) => s.id === stationId);
    if (!st || stationFobShiftSilenceMinutes(st) === minutes) return;
    try {
      setStationSilenceSaveBusyId(stationId);
      await stationsApi.updateStation(siteId, stationId, { fobShiftSilenceMinutes: minutes });
      await loadData();
    } catch (e: unknown) {
      showError(formatStationsApiError(e));
      if (st) setSettingsSilenceMinutes(stationFobShiftSilenceMinutes(st));
    } finally {
      setStationSilenceSaveBusyId(null);
    }
  };

  const saveStationOfflineGraceMinutes = async (stationId: number, minutes: number) => {
    if (!siteId) return;
    const st = stations.find((s) => s.id === stationId);
    if (!st || stationFobShiftOfflineGraceMinutes(st) === minutes) return;
    try {
      setStationOfflineGraceSaveBusyId(stationId);
      await stationsApi.updateStation(siteId, stationId, { fobShiftOfflineGraceMinutes: minutes });
      await loadData();
    } catch (e: unknown) {
      showError(formatStationsApiError(e));
      if (st) setSettingsOfflineGraceMinutes(stationFobShiftOfflineGraceMinutes(st));
    } finally {
      setStationOfflineGraceSaveBusyId(null);
    }
  };

  const saveStationReopenWindowMinutes = async (stationId: number, minutes: number) => {
    if (!siteId) return;
    const st = stations.find((s) => s.id === stationId);
    if (!st || stationFobShiftReopenWindowMinutes(st) === minutes) return;
    try {
      setStationReopenWindowSaveBusyId(stationId);
      await stationsApi.updateStation(siteId, stationId, { fobShiftReopenWindowMinutes: minutes });
      await loadData();
    } catch (e: unknown) {
      showError(formatStationsApiError(e));
      if (st) setSettingsReopenWindowMinutes(stationFobShiftReopenWindowMinutes(st));
    } finally {
      setStationReopenWindowSaveBusyId(null);
    }
  };

  const setStationShiftsActiveById = async (stationId: number, active: boolean) => {
    if (!siteId) return;
    const st = stations.find((s) => s.id === stationId);
    if (!st || stationShiftsActive(st) === active) return;
    try {
      setStationShiftsToggleBusyId(stationId);
      await stationsApi.updateStation(siteId, stationId, { blockFobShiftAutomation: !active });
      await loadData();
    } catch (e: unknown) {
      showError(formatStationsApiError(e));
    } finally {
      setStationShiftsToggleBusyId(null);
    }
  };

  const setFobShiftsActive = async (keyfobUid: string, active: boolean) => {
    if (!siteId) return;
    const blocked = !active;
    try {
      setFobShiftBusyUid(keyfobUid);
      await stationsApi.setKeyfobBlocked(siteId, keyfobUid, blocked);
      await loadData();
    } catch (e: unknown) {
      showError(formatStationsApiError(e));
    } finally {
      setFobShiftBusyUid(null);
    }
  };

  const copyDeviceUidFromSettings = async () => {
    if (!settingsStation) return;
    const ok = await copyText(settingsStation.deviceUid);
    if (ok) window.alert('Device UID скопирован в буфер.');
  };

  const onlineCount = useMemo(() => {
    return stations.filter((s) => stationHeartbeatFresh(s.lastSeenAt)).length;
  }, [stations]);

  /** Брелки из кэша сервера (heartbeat), с указанием станции. */
  const keyfobPickOptions = useMemo(() => {
    const rows: {
      stationName: string;
      keyfobUid: string;
      label?: string | null;
      isBlocked: boolean;
      rfobBleOnline: boolean;
      lastSeenUtc?: string | null;
    }[] = [];
    for (const s of stations) {
      const fobs = s.registeredFobs ?? [];
      for (const f of fobs) {
        const uid = String(f.keyfobUid ?? '')
          .trim()
          .replace(/^0x/i, '')
          .toUpperCase();
        if (uid.length >= 4) {
          const linkOk = stationHeartbeatFresh(s.lastSeenAt);
          const displayLabel = sanitizeKeyfobDisplayLabel(f.label);
          const lastSeen = f.lastBleSeenUtc ?? (linkOk ? s.lastSeenAt : null) ?? undefined;
          rows.push({
            stationName: s.name,
            keyfobUid: uid,
            label: displayLabel === null ? undefined : displayLabel,
            isBlocked: Boolean(f.isBlocked),
            rfobBleOnline: linkOk && Boolean(f.online) && presenceFresh(lastSeen),
            lastSeenUtc: lastSeen,
          });
        }
      }
    }
    return rows;
  }, [stations]);

  const hasKeyfobPickList = keyfobPickOptions.length > 0;

  /** Anti-lost теги (watchlist r-station) из кэша heartbeat. */
  const antilostPickOptions = useMemo(() => {
    const rows: {
      stationName: string;
      bleAddress: string;
      label?: string | null;
      online?: boolean | null;
      lastSeenUtc?: string | null;
      isBlocked: boolean;
    }[] = [];
    for (const s of stations) {
      const tags = s.antilostTags ?? [];
      for (const t of tags) {
        const addr = String(t.bleAddress ?? '')
          .trim()
          .toUpperCase();
        if (isHeartbeatBleMac(addr)) {
          const linkOk = stationHeartbeatFresh(s.lastSeenAt);
          rows.push({
            stationName: s.name,
            bleAddress: addr,
            label: t.label,
            online: linkOk ? (t.online ?? false) : false,
            lastSeenUtc: t.lastSeenUtc ?? undefined,
            isBlocked: Boolean(t.isBlocked),
          });
        }
      }
    }
    return rows;
  }, [stations]);

  const hasAntilostPickList = antilostPickOptions.length > 0;

  const assignmentByFobUid = useMemo(() => {
    const m = new Map<string, KeyfobAssignment>();
    for (const a of keyfobAssignments) {
      const uid = String(a.keyfobUid)
        .trim()
        .replace(/^0x/i, '')
        .toUpperCase();
      if (uid && !m.has(uid)) m.set(uid, a);
    }
    return m;
  }, [keyfobAssignments]);

  const assignedKeyfobUids = useMemo(
    () =>
      new Set(
        keyfobAssignments.map((a) => String(a.keyfobUid).trim().replace(/^0x/i, '').toUpperCase())
      ),
    [keyfobAssignments]
  );

  const unboundFobOptions: UnboundPickRow[] = useMemo(() => {
    const rf: UnboundPickRow[] = keyfobPickOptions
      .filter((o) => !assignedKeyfobUids.has(o.keyfobUid))
      .map((o) => ({
        kind: 'rfob',
        stationName: o.stationName,
        keyfobUid: o.keyfobUid,
        label: o.label,
      }));
    const al: UnboundPickRow[] = antilostPickOptions
      .filter((o) => !assignedKeyfobUids.has(credentialUidFromBle(o.bleAddress)))
      .map((o) => ({
        kind: 'antilost',
        stationName: o.stationName,
        keyfobUid: credentialUidFromBle(o.bleAddress),
        label: o.label,
      }));
    return [...rf, ...al];
  }, [keyfobPickOptions, antilostPickOptions, assignedKeyfobUids]);

  const assignmentByExecutorId = useMemo(() => {
    const m = new Map<number, KeyfobAssignment>();
    for (const a of keyfobAssignments) m.set(a.siteExecutorId, a);
    return m;
  }, [keyfobAssignments]);

  /** Последний сигнал по UID брелка / anti-lost MAC из кэша heartbeat. */
  const credentialPresenceByUid = useMemo(() => {
    const m = new Map<string, PresenceSnap>();
    for (const s of stations) {
      const linkOk = stationHeartbeatFresh(s.lastSeenAt);
      for (const f of s.registeredFobs ?? []) {
        const uid = String(f.keyfobUid ?? '')
          .trim()
          .replace(/^0x/i, '')
          .toUpperCase();
        if (uid.length < 4) continue;
        const lastSeen = f.lastBleSeenUtc ?? (linkOk ? s.lastSeenAt : null) ?? null;
        upsertCredentialPresence(m, uid, {
          online: linkOk && Boolean(f.online) && presenceFresh(lastSeen),
          lastSeenUtc: lastSeen,
          source: 'rfob',
        });
      }
      for (const t of s.antilostTags ?? []) {
        const addr = String(t.bleAddress ?? '')
          .trim()
          .toUpperCase();
        if (!isHeartbeatBleMac(addr)) continue;
        const uid = credentialUidFromBle(addr);
        const lastSeen = t.lastSeenUtc ?? null;
        upsertCredentialPresence(m, uid, {
          online: linkOk && Boolean(t.online) && presenceFresh(lastSeen),
          lastSeenUtc: lastSeen,
          source: 'antilost',
        });
      }
    }
    return m;
  }, [stations]);

  /** Присутствие с телефона (mobile_beacon_presence) по userId. */
  const mobilePresenceByUserId = useMemo(() => {
    const granted = new Map<number, string>();
    const left = new Map<number, string>();
    for (const ev of events) {
      if (ev.eventType !== 'mobile_beacon_presence') continue;
      const match = String(ev.keyfobUid ?? '').match(/^MOBILE:(\d+)$/i);
      if (!match) continue;
      const userId = Number(match[1]);
      if (!Number.isFinite(userId) || userId <= 0) continue;
      const ts = ev.receivedAt || ev.occurredAtDevice;
      if (!ts) continue;
      if (ev.accessDecision === 'granted') {
        const prev = granted.get(userId);
        if (!prev || new Date(ts).getTime() > new Date(prev).getTime()) granted.set(userId, ts);
      } else if (ev.accessDecision === 'left') {
        const prev = left.get(userId);
        if (!prev || new Date(ts).getTime() > new Date(prev).getTime()) left.set(userId, ts);
      }
    }
    const m = new Map<number, PresenceSnap>();
    granted.forEach((gts, userId) => {
      const lts = left.get(userId);
      const inZone = !lts || new Date(gts).getTime() > new Date(lts).getTime();
      m.set(userId, {
        online: inZone && presenceFresh(gts, MOBILE_PRESENCE_ONLINE_FRESH_MS),
        lastSeenUtc: gts,
        source: 'mobile',
      });
    });
    return m;
  }, [events]);

  const presenceByExecutorId = useMemo(() => {
    const m = new Map<number, PresenceSnap>();
    for (const ex of executors) {
      const assignment = assignmentByExecutorId.get(ex.id);
      let snap: PresenceSnap | null = null;
      if (assignment) {
        const uid = String(assignment.keyfobUid)
          .trim()
          .replace(/^0x/i, '')
          .toUpperCase();
        snap = credentialPresenceByUid.get(uid) ?? null;
      }
      if (ex.userId) {
        snap = mergePresenceSnapshots(snap, mobilePresenceByUserId.get(ex.userId) ?? null);
      }
      if (snap) m.set(ex.id, snap);
    }
    return m;
  }, [executors, assignmentByExecutorId, credentialPresenceByUid, mobilePresenceByUserId]);

  const userNameByUserId = useMemo(() => {
    const m = new Map<number, string>();
    for (const ex of executors) {
      if (ex.userId) m.set(ex.userId, ex.userName || ex.displayName);
    }
    for (const a of keyfobAssignments) {
      if (a.userId) m.set(a.userId, a.executorName);
    }
    return m;
  }, [executors, keyfobAssignments]);

  const displayEvents = events;

  const openAddModal = () => {
    setFormName('');
    setFormBeaconId('');
    setFormApiKey('');
    setShowAddModal(true);
  };

  const closeAddModal = () => {
    if (submitting) return;
    setShowAddModal(false);
  };

  const onCreateStation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!siteId) return;
    const name = formName.trim();
    const beaconPublicId = formBeaconId.trim().toUpperCase();
    const apiKey = formApiKey.trim();
    if (!name) {
      window.alert('Введите название станции.');
      return;
    }
    if (!beaconPublicId && !apiKey) {
      window.alert('Укажите ID маяка (6 символов, напр. B23810) или API-ключ для WiFi.');
      return;
    }
    if (beaconPublicId && !/^[0-9A-F]{6}$/.test(beaconPublicId)) {
      window.alert('ID маяка: 6 hex-символов (0-9, A-F), как в BLE «R-Station B23810».');
      return;
    }
    try {
      setSubmitting(true);
      const res = await stationsApi.createStation(siteId, {
        name,
        beaconPublicId: beaconPublicId || undefined,
        apiKey: apiKey || undefined,
      });
      setShowAddModal(false);
      const serverSecret = res.beaconSecret?.trim();
      if (serverSecret) {
        setSecretShown({
          title: 'Маяк зарегистрирован',
          apiKey: serverSecret,
          beaconPublicId,
          hint: `ID маяка ${beaconPublicId}. Скопируйте секрет (64 hex) в портал станции: зажать кнопку 3 сек → «Секрет маяка».${apiKey ? ' API-ключ — для WiFi.' : ''}`,
        });
      } else if (apiKey) {
        setSecretShown({
          title: 'Станция создана',
          apiKey,
          hint: 'В портале станции (зажать кнопку 3 сек) вставьте API-ключ.',
        });
      }
      await loadData();
    } catch (err: any) {
      const d = err?.response?.data;
      let msg: string;
      if (typeof d === 'string') {
        msg = d;
      } else if (d && typeof d.errors === 'object' && d.errors !== null) {
        const parts = Object.values(d.errors)
          .flat()
          .filter((x): x is string => typeof x === 'string' && x.length > 0);
        msg = parts.join('\n') || (typeof d.title === 'string' ? d.title : 'Ошибка создания');
      } else {
        msg = (d && (d.message || d.title)) || err?.message || 'Ошибка создания';
      }
      window.alert(String(msg));
    } finally {
      setSubmitting(false);
    }
  };

  const tryOpenBindSheet = (executorId: number) => {
    if (!hasKeyfobPickList && !hasAntilostPickList) {
      window.alert('Сначала дождитесь брелков R‑FOB или anti-lost тегов на станциях (heartbeat) и нажмите «Обновить».');
      return;
    }
    if (unboundFobOptions.length === 0) {
      window.alert(
        'Нет свободных брелков и тегов: все известные серверу ключи (R‑FOB и anti-lost MAC) уже привязаны к исполнителям.'
      );
      return;
    }
    setBindExecutorId(executorId);
  };

  const assignFobToExecutor = async (executorId: number, uid: string, label: string | null | undefined) => {
    if (!siteId) return;
    try {
      setKfSubmitting(true);
      await stationsApi.assignKeyfob(siteId, {
        keyfobUid: uid,
        keyfobLabel: (label && String(label).trim()) || undefined,
        siteExecutorId: executorId,
      });
      setBindExecutorId(null);
      await loadData();
    } catch (err: any) {
      const d = err?.response?.data;
      const msg = typeof d === 'string' ? d : d?.message || err?.message || 'Ошибка привязки';
      window.alert(String(msg));
    } finally {
      setKfSubmitting(false);
    }
  };

  const closeBindSheet = () => {
    if (kfSubmitting) return;
    setBindExecutorId(null);
  };

  const onUnassignKeyfob = async (a: KeyfobAssignment) => {
    if (!siteId) return;
    const ok = window.confirm(
      `Снять привязку брелка ${a.keyfobUid} с ${a.executorName}?`
    );
    if (!ok) return;
    try {
      setKfSubmitting(true);
      await stationsApi.unassignKeyfob(siteId, a.keyfobId);
      await loadData();
    } catch (err: any) {
      const d = err?.response?.data;
      const msg = typeof d === 'string' ? d : d?.message || err?.message || 'Ошибка';
      window.alert(String(msg));
    } finally {
      setKfSubmitting(false);
    }
  };

  const apiBaseUrl = config.api.baseURL.replace('/api/v1', '').replace('/api/v2', '');

  /** Тот же маппинг, что в ExecutorSelector: SiteExecutor → User + колонка привязки брелка */
  const executorBindUserList: UserListUser[] = executors.map((ex) => {
    const a = assignmentByExecutorId.get(ex.id);
    const boundFobLabel = a ? sanitizeKeyfobDisplayLabel(a.keyfobLabel) : null;
    const displayName = ex.userName || ex.displayName || `Исполнитель ${ex.id}`;
    const presence = presenceByExecutorId.get(ex.id);
    const presenceTitle = presence
      ? presence.source === 'mobile'
        ? presence.online
          ? 'Телефон в зоне BLE-маяка'
          : 'Последний отчёт с телефона вне окна присутствия'
        : presence.source === 'antilost'
          ? presence.online
            ? 'Anti-lost метка в зоне станции'
            : 'Anti-lost не видна по данным станции'
          : presence.online
            ? 'R‑FOB в BLE-зоне станции'
            : 'R‑FOB не виден по BLE на станции'
      : undefined;
    return {
      userId: ex.id.toString(),
      userName: displayName + (ex.isExternal ? ' (внешний)' : ''),
      userRole: ex.isExternal ? 'Внешний исполнитель' : 'Исполнитель',
      avatarColor: getExecutorAvatarColor(ex.id),
      avatarUrl: ex.avatarUrl
        ? ex.avatarUrl.startsWith('http')
          ? ex.avatarUrl
          : `${apiBaseUrl}${ex.avatarUrl}`
        : undefined,
      disableLongPress: true,
      presenceOnline: presence ? presence.online : undefined,
      presenceLastSeenLabel: presence?.lastSeenUtc ? formatLastSeen(presence.lastSeenUtc) : null,
      presenceTitle,
      trailing: a ? (
        <div className="executor-bind-assigned">
          <span className="mono executor-bind-uid" title={a.keyfobUid}>
            {boundFobLabel ? `${boundFobLabel} · ${a.keyfobUid}` : a.keyfobUid}
          </span>
          <button
            type="button"
            className="station-btn secondary executor-bind-unbind"
            disabled={kfSubmitting}
            onClick={() => onUnassignKeyfob(a)}
          >
            Снять
          </button>
        </div>
      ) : (
        <button
          type="button"
          className="station-btn primary executor-bind-cta"
          disabled={loading || kfSubmitting}
          onClick={() => tryOpenBindSheet(ex.id)}
        >
          Привязать ключ
        </button>
      ),
    };
  });

  if (!siteId || hasSiteEditPermission !== true) {
    return null;
  }

  return (
    <DashboardCard
      id="stations-card"
      title="Станции"
      icon="ri-base-station-line"
      isCollapsible
      isCollapsed={isCollapsed}
      onToggleCollapse={() => setIsCollapsed(!isCollapsed)}
      expandMode="fullscreen"
      onFullscreenOpenChange={setIsFullscreenOpen}
      counters={siteId ? [onlineCount, stations.length] : undefined}
      headerActions={
        siteId ? (
          <span className="stations-card-header-actions" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="card-action-btn"
              onClick={openAddModal}
              title="Добавить станцию"
            >
              <i className="ri-add-line" />
            </button>
            <button
              type="button"
              className="card-action-btn"
              onClick={() => {
                loadData();
              }}
              disabled={loading}
              title="Обновить"
            >
              <i className={loading ? 'ri-loader-4-line ri-spin' : 'ri-refresh-line'} />
            </button>
          </span>
        ) : null
      }
    >
      {!siteId ? (
        <div className="stations-card-empty">Выберите объект</div>
      ) : (
        <div className="stations-card-content">
          {error && <div className="stations-card-error">{error}</div>}

          {loading && <div className="stations-card-loading">Загрузка…</div>}

          <div className={loading ? 'stations-card-faded' : ''}>
            <HorizontalCategoryTabs
              className="stations-card-tabs"
              categories={Array.from(STATIONS_TABS)}
              value={activeTab}
              onChange={(c) => c && (STATIONS_TABS as readonly string[]).includes(c) && setActiveTab(c as StationsTab)}
              showAllOption={false}
              size="small"
            />

            {activeTab === 'Брелки' && (
              <div className="stations-tab-panel" aria-label="Брелки">
                {keyfobPickOptions.length === 0 && antilostPickOptions.length === 0 && !loading && (
                  <p className="stations-card-empty">
                    Пока нет брелков R-FOB и anti-lost тегов в кэше сервера. Зарегистрируйте брелок или добавьте тег в
                    watchlist на станции и дождитесь heartbeat.
                  </p>
                )}
                <ul className="stations-fobs-tab-list">
                  {keyfobPickOptions.map((o) => {
                    const assign = assignmentByFobUid.get(o.keyfobUid);
                    const fobBusy = fobShiftBusyUid === o.keyfobUid;
                    return (
                      <li className="stations-fob-tab-row" key={`${o.stationName}-${o.keyfobUid}`}>
                        <div className="stations-fob-tab-main">
                          <div className="stations-fob-tab-fob-line">
                            <PresenceDot
                              online={o.rfobBleOnline}
                              title={
                                o.rfobBleOnline
                                  ? 'R‑FOB в BLE-зоне станции (видна реклама)'
                                  : 'R‑FOB не виден по BLE на станции (или станция ещё не прислала состояние)'
                              }
                            />
                            <span className="stations-fob-tab-fob-k">Брелок</span>
                            <span className="mono stations-fob-tab-uid stations-fob-tab-uid--primary" title="Брелок">
                              {o.label ? (
                                <>
                                  <span className="stations-fob-tab-label">{o.label}</span>
                                  <span> · {o.keyfobUid}</span>
                                </>
                              ) : (
                                o.keyfobUid
                              )}
                            </span>
                            {formatLastSeen(o.lastSeenUtc) && (
                              <span className="stations-fob-tab-lastseen-inline">{formatLastSeen(o.lastSeenUtc)}</span>
                            )}
                          </div>
                          <div className="stations-fob-tab-station-line">
                            <span className="stations-fob-tab-station-k">Станция</span>
                            <span className="stations-fob-tab-station-name">{o.stationName}</span>
                          </div>
                        </div>
                        <div
                          className={
                            assign
                              ? 'stations-fob-tab-assign stations-fob-tab-assign--bound'
                              : 'stations-fob-tab-assign stations-fob-tab-assign--free'
                          }
                        >
                          {assign ? (
                            <div className="stations-fob-tab-assign-block">
                              <span className="stations-fob-tab-assign-hint">Привязано к</span>
                              <span className="stations-fob-tab-assign-name">{assign.executorName}</span>
                            </div>
                          ) : (
                            <span className="stations-fob-tab-assign-free-text">Не привязан к исполнителю</span>
                          )}
                        </div>
                        <div className="stations-fob-tab-shift">
                          <span className="stations-fob-tab-shift-k">Активен</span>
                          <label
                            className="station-power-toggle station-power-toggle--compact"
                            title="Вкл — учитывать брелок для автосмен. Выкл — только события в логе."
                            onClick={(e) => e.stopPropagation()}
                          >
                            <input
                              type="checkbox"
                              className="station-power-toggle-input"
                              checked={!o.isBlocked}
                              onChange={(e) => {
                                void setFobShiftsActive(o.keyfobUid, e.target.checked);
                              }}
                              disabled={loading || kfSubmitting || fobBusy}
                              aria-label={
                                o.isBlocked ? 'Включить брелок для автосмен' : 'Выключить брелок для автосмен'
                              }
                            />
                            <span className="station-power-toggle-track" aria-hidden>
                              <span className="station-power-toggle-knob" />
                            </span>
                          </label>
                        </div>
                      </li>
                    );
                  })}
                </ul>
                {hasAntilostPickList && (
                  <>
                    <h4 className="stations-fobs-antilost-head">Anti-lost (iTag / Lenze), кэш heartbeat — как R‑FOB: привязка, «Активен», лог и смены по MAC</h4>
                    <ul className="stations-fobs-tab-list stations-fobs-tab-list--antilost">
                      {antilostPickOptions.map((o) => {
                        const uid = credentialUidFromBle(o.bleAddress);
                        const assign = assignmentByFobUid.get(uid);
                        const fobBusy = fobShiftBusyUid === uid;
                        return (
                          <li className="stations-fob-tab-row" key={`${o.stationName}-${uid}`}>
                            <div className="stations-fob-tab-main">
                              <div className="stations-fob-tab-fob-line">
                                <PresenceDot
                                  online={Boolean(o.online)}
                                  title={
                                    o.online === true
                                      ? 'Anti-lost в окне последнего вида по данным станции'
                                      : o.online === false
                                        ? 'Нет вида по настройке «онлайн» на станции'
                                        : 'Нет признака online в кэше'
                                  }
                                />
                                <span className="stations-fob-tab-fob-k">Anti-lost</span>
                                <span className="mono stations-fob-tab-uid stations-fob-tab-uid--primary" title="MAC = идентификатор на сервере">
                                  {o.label ? (
                                    <>
                                      <span className="stations-fob-tab-label">{o.label}</span>
                                      <span> · {uid}</span>
                                    </>
                                  ) : (
                                    uid
                                  )}
                                </span>
                                {formatLastSeen(o.lastSeenUtc) && (
                                  <span className="stations-fob-tab-lastseen-inline">{formatLastSeen(o.lastSeenUtc)}</span>
                                )}
                              </div>
                              <div className="stations-fob-tab-station-line">
                                <span className="stations-fob-tab-station-k">Станция</span>
                                <span className="stations-fob-tab-station-name">{o.stationName}</span>
                              </div>
                            </div>
                            <div
                              className={
                                assign
                                  ? 'stations-fob-tab-assign stations-fob-tab-assign--bound'
                                  : 'stations-fob-tab-assign stations-fob-tab-assign--free'
                              }
                            >
                              {assign ? (
                                <div className="stations-fob-tab-assign-block">
                                  <span className="stations-fob-tab-assign-hint">Привязано к</span>
                                  <span className="stations-fob-tab-assign-name">{assign.executorName}</span>
                                </div>
                              ) : (
                                <span className="stations-fob-tab-assign-free-text">
                                  Не привязан — вкладка «Привязка»
                                </span>
                              )}
                            </div>
                            <div className="stations-fob-tab-shift">
                              <span className="stations-fob-tab-shift-k">Активен</span>
                              <label
                                className="station-power-toggle station-power-toggle--compact"
                                title="Вкл — учитывать метку для автосмен по событию presence. Выкл — события всё равно в логе, смены не трогаем."
                                onClick={(e) => e.stopPropagation()}
                              >
                                <input
                                  type="checkbox"
                                  className="station-power-toggle-input"
                                  checked={!o.isBlocked}
                                  onChange={(e) => {
                                    void setFobShiftsActive(uid, e.target.checked);
                                  }}
                                  disabled={loading || kfSubmitting || fobBusy}
                                  aria-label={
                                    o.isBlocked ? 'Включить anti-lost для автосмен' : 'Выключить anti-lost для автосмен'
                                  }
                                />
                                <span className="station-power-toggle-track" aria-hidden>
                                  <span className="station-power-toggle-knob" />
                                </span>
                              </label>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </>
                )}
              </div>
            )}

            {activeTab === 'Станции' && (
              <div className="stations-tab-panel" aria-label="Станции">
                <div className="stations-list-region" aria-label="Список станций">
                  <div className="stations-list">
                    {stations.length === 0 && !loading && <div className="stations-card-empty">Станции пока не добавлены</div>}
                    {stations.map((station) => {
                      const shiftsOn = stationShiftsActive(station);
                      const shiftBusy = stationShiftsToggleBusyId === station.id;
                      const staFresh = stationHeartbeatFresh(station.lastSeenAt);
                      const rfN = (station.registeredFobs ?? []).filter((f) => {
                        const uid = String(f.keyfobUid ?? '')
                          .trim()
                          .replace(/^0x/i, '');
                        return uid.length >= 4;
                      }).length;
                      const alN = (station.antilostTags ?? []).filter((t) =>
                        isHeartbeatBleMac(String(t.bleAddress ?? ''))
                      ).length;
                      return (
                        <div
                          className={`station-block ${shiftsOn ? 'station-block--on' : 'station-block--off'}`}
                          key={station.id}
                        >
                          <div className="station-list-row">
                            <button
                              type="button"
                              className="station-row station-row--clickable station-list-row-open"
                              onClick={() => openStationSettings(station)}
                              title="Настройки станции"
                              aria-label={`Настройки станции: ${station.name}`}
                            >
                              <div className="station-name">
                                <PresenceDot
                                  online={staFresh}
                                  title={
                                    staFresh
                                      ? station.beaconOnly
                                        ? `Присутствие с телефона (${station.lastSeenAt ? new Date(station.lastSeenAt).toLocaleString() : ''})`
                                        : `Станция на связи (heartbeat, ${station.lastSeenAt ? new Date(station.lastSeenAt).toLocaleString() : ''})`
                                      : station.beaconOnly
                                        ? 'Нет отчётов с телефонов у маяка'
                                        : 'Нет свежего heartbeat — проверьте станцию, API или интернет'
                                  }
                                />
                                <span className="station-name-k">Станция</span>
                                <span className="station-name-text">{station.name}</span>
                                {station.beaconPublicId ? (
                                  <span className="station-cache-meta">Маяк {station.beaconPublicId}</span>
                                ) : (
                                  <span className="station-cache-meta station-cache-meta--warn">ID маяка не задан</span>
                                )}
                                <span className="station-cache-meta">
                                  R‑FOB: {rfN}
                                  {' · '}
                                  Anti-lost: {alN}
                                </span>
                              </div>
                            </button>
                            <div
                              className="station-list-row-toggle-wrap"
                              onClick={(e) => e.stopPropagation()}
                              onKeyDown={(e) => e.stopPropagation()}
                              role="presentation"
                            >
                              <span className="station-list-row-toggle-label">Активен</span>
                              <label
                                className="station-power-toggle station-power-toggle--compact"
                                title="Вкл — учёт смен по брелку с этой станции. Выкл — только события в лог."
                              >
                                <input
                                  type="checkbox"
                                  className="station-power-toggle-input"
                                  checked={shiftsOn}
                                  onChange={(e) => {
                                    void setStationShiftsActiveById(station.id, e.target.checked);
                                  }}
                                  disabled={loading || shiftBusy}
                                  aria-label={
                                    shiftsOn
                                      ? 'Выключить учёт смен по брелку с этой станции'
                                      : 'Включить учёт смен по брелку с этой станции'
                                  }
                                />
                                <span className="station-power-toggle-track" aria-hidden>
                                  <span className="station-power-toggle-knob" />
                                </span>
                              </label>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'Привязка' && (
              <div className="stations-tab-panel" aria-label="Привязка брелков">
                {executors.length > 0 ? (
                  <p className="stations-bind-presence-hint">
                    Зелёная точка — в зоне станции (брелок, anti-lost или телефон с маяком). Рядом — время последнего сигнала.
                  </p>
                ) : null}
                {executors.length === 0 && !loading && (
                  <p className="stations-card-empty">На объекте нет исполнителей — добавьте участников в настройках объекта.</p>
                )}
                {executors.length > 0 ? (
                  <UserList
                    className="stations-executor-bind-user-list"
                    users={executorBindUserList}
                    showRemoveButton={false}
                    canManagePermissions={false}
                    canManageMembers={false}
                    preserveOrder
                  />
                ) : null}
              </div>
            )}

            {activeTab === 'Лог' && (
              <div className="stations-tab-panel" aria-label="События">
                {displayEvents.length === 0 && !loading && <div className="stations-card-empty">Нет событий</div>}
                <div className="stations-events">
                  {displayEvents
                    .filter((ev) => ev.eventType === 'mobile_beacon_presence')
                    .sort((a, b) => new Date(b.receivedAt).getTime() - new Date(a.receivedAt).getTime())
                    .map((ev) => {
                      const inZone = ev.accessDecision === 'granted';
                      const receivedMs = new Date(ev.receivedAt).getTime();
                      const occurredMs = ev.occurredAtDevice ? new Date(ev.occurredAtDevice).getTime() : null;
                      const scanDiff = occurredMs !== null ? receivedMs - occurredMs : null;
                      const showScan = scanDiff !== null && scanDiff > 60_000 && scanDiff < 3_600_000;
                      return (
                        <div className="station-event-row2" key={ev.id}>
                          <time className="station-ev-time" dateTime={ev.receivedAt}>
                            {formatEventTime(ev.receivedAt)}
                          </time>
                          <span className="station-ev-name">
                            {formatEventActorName(ev.keyfobUid, userNameByUserId)}
                          </span>
                          <span className={`station-ev-status ${inZone ? 'station-ev-inzone' : 'station-ev-left'}`}>
                            {inZone ? '● в зоне' : '○ вышел'}
                          </span>
                          {showScan && (
                            <span className="station-ev-scan">
                              скан: <time dateTime={ev.occurredAtDevice!}>{formatEventTime(ev.occurredAtDevice)}</time>
                            </span>
                          )}
                        </div>
                      );
                    })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <BottomSheet
        isOpen={settingsForStationId != null && siteId != null}
        onClose={closeStationSettings}
        title="Настройки станции"
        size="large"
        elevated
      >
        {settingsStation ? (
          <div className="stations-settings-body">
              <label className="stations-settings-field">
                <span>Название</span>
                <div className="stations-settings-name-row">
                  <input
                    type="text"
                    value={settingsNameDraft}
                    onChange={(e) => setSettingsNameDraft(e.target.value)}
                    autoComplete="off"
                    disabled={settingsSaveBusy}
                  />
                  <button
                    type="button"
                    className="station-btn primary"
                    onClick={saveStationName}
                    disabled={
                      settingsSaveBusy ||
                      !settingsNameDraft.trim() ||
                      settingsNameDraft.trim() === settingsStation.name
                    }
                  >
                    {settingsSaveBusy ? '…' : 'Сохранить'}
                  </button>
                </div>
              </label>
              <label className="stations-settings-field">
                <span>ID маяка (6 hex)</span>
                <div className="stations-settings-name-row">
                  <input
                    type="text"
                    value={settingsBeaconDraft}
                    onChange={(e) => setSettingsBeaconDraft(e.target.value.toUpperCase())}
                    placeholder="B23810"
                    autoComplete="off"
                    spellCheck={false}
                    disabled={settingsSaveBusy}
                  />
                  <button
                    type="button"
                    className="station-btn primary"
                    onClick={saveStationBeaconId}
                    disabled={
                      settingsSaveBusy ||
                      settingsBeaconDraft.trim().toUpperCase() === (settingsStation.beaconPublicId ?? '').toUpperCase()
                    }
                  >
                    {settingsSaveBusy ? '…' : 'Сохранить'}
                  </button>
                </div>
                <span className="stations-field-hint">
                  Как в BLE «R-Station B23810». Можно изменить у уже созданной станции. ID уникален в системе.
                </span>
              </label>
              <div className="stations-settings-row">
                <span className="stations-settings-label">Активен</span>
                <label
                  className="station-power-toggle"
                  title="Включено — открываем и продлеваем смены по брелку с этой станции. Выключено — события в лог остаются, автосмены не трогаем."
                >
                  <input
                    type="checkbox"
                    className="station-power-toggle-input"
                    checked={stationShiftsActive(settingsStation)}
                    onChange={(e) => {
                      void setStationShiftsActiveById(settingsStation.id, e.target.checked);
                    }}
                    disabled={stationShiftsToggleBusyId === settingsStation.id}
                    aria-label={
                      stationShiftsActive(settingsStation)
                        ? 'Выключить автосмены по брелку с этой станции'
                        : 'Включить автосмены по брелку с этой станции'
                    }
                  />
                  <span className="station-power-toggle-track" aria-hidden>
                    <span className="station-power-toggle-knob" />
                  </span>
                </label>
              </div>
              <div className="stations-settings-row">
                <span className="stations-settings-label">Закрыть смену через</span>
                <select
                  className="stations-settings-select"
                  value={settingsSilenceMinutes}
                  disabled={
                    !stationShiftsActive(settingsStation) ||
                    stationSilenceSaveBusyId === settingsStation.id
                  }
                  title={
                    stationShiftsActive(settingsStation)
                      ? 'Сколько минут ждать после последнего сигнала брелка с этой станции'
                      : 'Включите автосмены, чтобы настроить таймаут'
                  }
                  aria-label="Минут до автозакрытия смены после пропажи брелка"
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    setSettingsSilenceMinutes(v);
                    void saveStationSilenceMinutes(settingsStation.id, v);
                  }}
                >
                  {FOB_SHIFT_SILENCE_MINUTES_OPTIONS.map((m) => (
                    <option key={m} value={m}>
                      {m} мин
                    </option>
                  ))}
                </select>
              </div>
              <div className="stations-settings-row">
                <span className="stations-settings-label">Закрыть при offline через</span>
                <select
                  className="stations-settings-select"
                  value={settingsOfflineGraceMinutes}
                  disabled={
                    !stationShiftsActive(settingsStation) ||
                    stationOfflineGraceSaveBusyId === settingsStation.id
                  }
                  title={
                    stationShiftsActive(settingsStation)
                      ? 'Если станция без heartbeat, ждать столько минут после тишины брелка'
                      : 'Включите автосмены, чтобы настроить таймаут'
                  }
                  aria-label="Минут offline станции до автозакрытия"
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    setSettingsOfflineGraceMinutes(v);
                    void saveStationOfflineGraceMinutes(settingsStation.id, v);
                  }}
                >
                  {FOB_SHIFT_OFFLINE_GRACE_MINUTES_OPTIONS.map((m) => (
                    <option key={m} value={m}>
                      {m} мин
                    </option>
                  ))}
                </select>
              </div>
              <div className="stations-settings-row">
                <span className="stations-settings-label">Переоткрыть смену в течение</span>
                <select
                  className="stations-settings-select"
                  value={settingsReopenWindowMinutes}
                  disabled={
                    !stationShiftsActive(settingsStation) ||
                    stationReopenWindowSaveBusyId === settingsStation.id
                  }
                  title={
                    stationShiftsActive(settingsStation)
                      ? 'После автозакрытия scan в этом окне продолжит ту же смену'
                      : 'Включите автосмены, чтобы настроить таймаут'
                  }
                  aria-label="Минут после автозакрытия для переоткрытия смены"
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    setSettingsReopenWindowMinutes(v);
                    void saveStationReopenWindowMinutes(settingsStation.id, v);
                  }}
                >
                  {FOB_SHIFT_REOPEN_WINDOW_MINUTES_OPTIONS.map((m) => (
                    <option key={m} value={m}>
                      {m} мин
                    </option>
                  ))}
                </select>
              </div>
              <p className="stations-settings-silence-hint">
                {stationShiftsActive(settingsStation)
                  ? 'После последнего подтверждённого сигнала брелка смена закроется автоматически. Если станция offline, закрытие отложится на указанный интервал.'
                  : 'Таймауты действуют, когда автосмены по станции включены.'}
              </p>
              <div className="stations-settings-code-row">
                <code className="stations-settings-uid" title="Device UID">
                  {settingsStation.deviceUid}
                </code>
                <button type="button" className="station-btn" onClick={copyDeviceUidFromSettings}>
                  Скопировать Device UID
                </button>
              </div>
              <div className="stations-settings-cache">
                <h4 className="stations-settings-cache-title">Кэш heartbeat со станции</h4>
                <p className="stations-settings-cache-hint">
                  Обновляется, когда r-station на связи и шлёт heartbeat (API-ключ, сеть).
                </p>
                <div className="stations-settings-cache-section">
                  <span className="stations-settings-cache-k">R‑FOB</span>
                  {(settingsStation.registeredFobs ?? []).length === 0 ? (
                    <p className="stations-settings-cache-empty">Пока нет — зарегистрируйте брелок на станции.</p>
                  ) : (
                    <ul className="stations-settings-cache-list">
                      {(settingsStation.registeredFobs ?? []).map((f) => {
                        const uid = String(f.keyfobUid ?? '')
                          .trim()
                          .replace(/^0x/i, '')
                          .toUpperCase();
                        const lbl = sanitizeKeyfobDisplayLabel(f.label);
                        return (
                          <li key={uid} className="stations-settings-cache-item">
                            <span className="mono" title={uid}>
                              {lbl ? `${lbl} · ${uid}` : uid}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
                <div className="stations-settings-cache-section">
                  <span className="stations-settings-cache-k">Anti-lost (iTag / Lenze)</span>
                  {(settingsStation.antilostTags ?? []).filter((t) =>
                    isHeartbeatBleMac(String(t.bleAddress ?? ''))
                  ).length === 0 ? (
                    <p className="stations-settings-cache-empty">
                      Пока нет — добавьте тег в watchlist на r-station (anti-lost).
                    </p>
                  ) : (
                    <ul className="stations-settings-cache-list">
                      {(settingsStation.antilostTags ?? [])
                        .filter((t) => isHeartbeatBleMac(String(t.bleAddress ?? '')))
                        .map((t) => {
                          const addr = String(t.bleAddress ?? '')
                            .trim()
                            .toUpperCase();
                          return (
                            <li key={addr} className="stations-settings-cache-item">
                              <span className="mono" title={addr}>
                                {t.label ? `${t.label} · ${addr}` : addr}
                              </span>
                              {t.online != null && (
                                <span className="stations-settings-cache-online muted small">
                                  {t.online ? 'в зоне' : 'вне окна'}
                                </span>
                              )}
                            </li>
                          );
                        })}
                    </ul>
                  )}
                </div>
              </div>
              <button
                type="button"
                className="station-btn full station-btn--danger"
                disabled={settingsSaveBusy}
                onClick={async () => {
                  if (!settingsStation || !siteId) return;
                  const ok = window.confirm(
                    `Удалить станцию «${settingsStation.name}»? Данные сохранятся, станция будет помечена как удалённая.`
                  );
                  if (!ok) return;
                  try {
                    setSettingsSaveBusy(true);
                    await stationsApi.deleteStation(siteId, settingsStation.id);
                    setSettingsForStationId(null);
                    await loadData();
                  } catch (e: any) {
                    const msg =
                      typeof e?.response?.data === 'string'
                        ? e.response.data
                        : e?.response?.data?.message || e?.message || 'Ошибка удаления';
                    window.alert(String(msg));
                  } finally {
                    setSettingsSaveBusy(false);
                  }
                }}
              >
                Удалить станцию
              </button>
              <button
                type="button"
                className="station-btn full"
                onClick={async () => {
                  if (!settingsStation || !siteId) return;
                  if (!settingsStation.beaconPublicId) {
                    window.alert('Сначала укажите ID маяка (B23810) в настройках станции.');
                    return;
                  }
                  const ok = window.confirm(
                    `Выдать новый секрет маяка для «${settingsStation.name}» (${settingsStation.beaconPublicId})? Старый перестанет работать. Counter на сервере сбросится.`
                  );
                  if (!ok) return;
                  try {
                    setSettingsSaveBusy(true);
                    const res = await stationsApi.regenerateBeaconSecret(siteId, settingsStation.id);
                    setSecretShown({
                      title: 'Новый секрет маяка',
                      apiKey: res.beaconSecret,
                      beaconPublicId: settingsStation.beaconPublicId ?? undefined,
                      hint: `ID ${settingsStation.beaconPublicId}. Вставьте 64 hex в портал станции (кнопка 3 сек → «Секрет маяка»). Без этого телефон получит «неверный код».`,
                    });
                    setSettingsForStationId(null);
                    await loadData();
                  } catch (e: any) {
                    const msg =
                      typeof e?.response?.data === 'string'
                        ? e.response.data
                        : e?.response?.data?.message || e?.message || 'Ошибка';
                    window.alert(String(msg));
                  } finally {
                    setSettingsSaveBusy(false);
                  }
                }}
                disabled={settingsSaveBusy || !settingsStation?.beaconPublicId}
              >
                Новый секрет маяка
              </button>
              <button
                type="button"
                className="station-btn full"
                onClick={async () => {
                  if (!settingsStation || !siteId) return;
                  const ok = window.confirm(
                    `Сгенерировать новый API-ключ? Старый перестанет работать. Станция «${settingsStation.name}».`
                  );
                  if (!ok) return;
                  try {
                    setSettingsSaveBusy(true);
                    const res = await stationsApi.rotateKey(siteId, settingsStation.id);
                    setSecretShown({
                      title: 'Новый ключ выдан',
                      deviceUid: settingsStation.deviceUid,
                      apiKey: res.apiKey,
                      hint: 'Введите ключ в r-station «Связь с сервером» или R_STATION_DEVICE_API_KEY.',
                    });
                    setSettingsForStationId(null);
                    await loadData();
                  } catch (e: any) {
                    const msg =
                      typeof e?.response?.data === 'string'
                        ? e.response.data
                        : e?.response?.data?.message || e?.message || 'Ошибка';
                    window.alert(String(msg));
                  } finally {
                    setSettingsSaveBusy(false);
                  }
                }}
                disabled={settingsSaveBusy}
              >
                Сгенерировать новый API-ключ
              </button>
          </div>
        ) : null}
      </BottomSheet>

      <BottomSheet
        isOpen={bindExecutorId != null && siteId != null}
        onClose={closeBindSheet}
        title="Выберите брелок или anti-lost"
        size="auto"
        elevated
      >
        <div className="stations-pick-fob-sheet">
          {(() => {
            const ex = bindExecutorId != null ? executors.find((e) => e.id === bindExecutorId) : null;
            return ex ? (
              <p className="stations-pick-fob-executor">
                Исполнитель: {ex.userName || ex.displayName || `Исполнитель ${ex.id}`}
              </p>
            ) : null;
          })()}
          {unboundFobOptions.length === 0 ? (
            <p className="stations-card-empty">Нет свободных R‑FOB или anti-lost MAC для привязки</p>
          ) : (
            <div className="stations-pick-fob-list" role="list">
              {unboundFobOptions.map((o) => (
                <button
                  key={`${o.kind}-${o.stationName}-${o.keyfobUid}`}
                  type="button"
                  className="stations-pick-fob-item"
                  role="listitem"
                  disabled={kfSubmitting}
                  onClick={() => {
                    if (bindExecutorId == null) return;
                    void assignFobToExecutor(bindExecutorId, o.keyfobUid, o.label);
                  }}
                >
                  <span className="stations-pick-fob-line0">
                    <span className="stations-pick-fob-kind">{o.kind === 'antilost' ? 'Anti-lost' : 'R‑FOB'}</span>
                  </span>
                  <span className="stations-pick-fob-line1">
                    <span className="stations-pick-fob-station-k">Станция</span>
                    <span className="stations-pick-fob-station-name">{o.stationName}</span>
                  </span>
                  <span className="mono stations-pick-fob-line2">
                    {o.label ? `${o.label} · ` : ''}
                    {o.keyfobUid}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </BottomSheet>

      {showAddModal && siteId && (
        <div className="stations-modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="stations-add-title">
          <div className="stations-modal">
            <div className="stations-modal-head">
              <h3 id="stations-add-title">Новая станция</h3>
              <button type="button" className="stations-modal-close" onClick={closeAddModal} aria-label="Закрыть">
                <i className="ri-close-line" />
              </button>
            </div>
            <form onSubmit={onCreateStation} className="stations-modal-form">
              <label className="stations-field">
                <span>Название</span>
                <input
                  type="text"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="Например, Проходная 1"
                  autoComplete="off"
                />
              </label>
              <label className="stations-field">
                <span>ID маяка (6 hex)</span>
                <input
                  type="text"
                  value={formBeaconId}
                  onChange={(e) => setFormBeaconId(e.target.value.toUpperCase())}
                  placeholder="B23810"
                  autoComplete="off"
                  spellCheck={false}
                />
                <span className="stations-field-hint">
                  Последние 3 байта MAC в BLE «R-Station B23810». Обязательно для маяка без WiFi.
                </span>
              </label>
              <label className="stations-field">
                <span>API-ключ (опционально, WiFi)</span>
                <div className="stations-apikey-row">
                  <input
                    type="text"
                    value={formApiKey}
                    onChange={(e) => setFormApiKey(e.target.value)}
                    autoComplete="off"
                    spellCheck={false}
                    placeholder="Только если станция на WiFi"
                  />
                  <button
                    type="button"
                    className="station-btn"
                    onClick={() => setFormApiKey(generateApiKey())}
                  >
                    Сгенерировать
                  </button>
                </div>
                <span className="stations-field-hint">Для heartbeat и брелков по WiFi. Для чистого маяка не нужен.</span>
              </label>
              <div className="stations-modal-actions">
                <button type="button" className="station-btn secondary" onClick={closeAddModal} disabled={submitting}>
                  Отмена
                </button>
                <button type="submit" className="station-btn primary" disabled={submitting}>
                  {submitting ? 'Создание…' : 'Создать'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {secretShown && (
        <div className="stations-modal-backdrop" role="dialog" aria-modal="true">
          <div className="stations-modal stations-modal--secret">
            <h3>{secretShown.title}</h3>
            <p className="stations-secret-warn">Скопируйте данные сейчас. Повторно ключ на сервере не показать.</p>
            <div className="stations-secret-block">
              {secretShown.deviceUid ? (
                <div className="stations-secret-line">
                  <span>Device UID (внутр.)</span>
                  <code>{secretShown.deviceUid}</code>
                  <button
                    type="button"
                    className="station-btn small"
                    onClick={async () => {
                      const ok = await copyText(secretShown.deviceUid!);
                      if (ok) window.alert('Скопировано');
                    }}
                  >
                    Копировать
                  </button>
                </div>
              ) : null}
              <div className="stations-secret-line">
                <span>{secretShown.beaconPublicId ? 'Секрет маяка' : 'API-ключ'}</span>
                <code className="stations-secret-key">{secretShown.apiKey}</code>
                <button
                  type="button"
                  className="station-btn small"
                  onClick={async () => {
                    const ok = await copyText(secretShown.apiKey);
                    if (ok) window.alert('Скопировано');
                  }}
                >
                  Копировать
                </button>
              </div>
            </div>
            {secretShown.hint && <p className="stations-secret-hint">{secretShown.hint}</p>}
            <button type="button" className="station-btn primary full" onClick={() => setSecretShown(null)}>
              Понятно
            </button>
          </div>
        </div>
      )}
    </DashboardCard>
  );
};

export default StationsCard;
